import React from 'react';
import { motion } from 'framer-motion';
import { LucideIcon } from 'lucide-react';

interface StyledIconProps {
  icon?: LucideIcon;
  src?: string; // For image-based icons
  alt?: string; // Alt text for image icons
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  color?: 'primary' | 'blue' | 'emerald' | 'cyan' | 'purple' | 'orange' | 'green';
  variant?: 'default' | 'static'; // static removes hover animations
  className?: string;
}

export const StyledIcon: React.FC<StyledIconProps> = ({
  icon: Icon,
  src,
  alt = 'Icon',
  size = 'md',
  color = 'primary',
  variant = 'default',
  className = ''
}) => {
  // Size configurations
  const sizeConfig = {
    xs: {
      container: 'w-8 h-8',
      icon: 'w-4 h-4'
    },
    sm: {
      container: 'w-12 h-12',
      icon: 'w-6 h-6'
    },
    md: {
      container: 'w-16 h-16',
      icon: 'w-8 h-8'
    },
    lg: {
      container: 'w-20 h-20',
      icon: 'w-10 h-10'
    },
    xl: {
      container: 'w-24 h-24',
      icon: 'w-12 h-12'
    }
  };

  // Color mapping to ensure all Tailwind classes are included
  const colorConfig = {
    primary: {
      iconBg: 'from-primary-500/20 to-primary-600/30',
      iconBorder: 'border-primary-400/30',
      iconText: 'text-primary-400',
      iconTextHover: 'text-primary-300',
      glowBg: 'from-primary-400/20 to-primary-600/30'
    },
    blue: {
      iconBg: 'from-blue-500/20 to-blue-600/30',
      iconBorder: 'border-blue-400/30',
      iconText: 'text-blue-400',
      iconTextHover: 'text-blue-300',
      glowBg: 'from-blue-400/20 to-blue-600/30'
    },
    emerald: {
      iconBg: 'from-emerald-500/20 to-emerald-600/30',
      iconBorder: 'border-emerald-400/30',
      iconText: 'text-emerald-400',
      iconTextHover: 'text-emerald-300',
      glowBg: 'from-emerald-400/20 to-emerald-600/30'
    },
    cyan: {
      iconBg: 'from-cyan-500/20 to-cyan-600/30',
      iconBorder: 'border-cyan-400/30',
      iconText: 'text-cyan-400',
      iconTextHover: 'text-cyan-300',
      glowBg: 'from-cyan-400/20 to-cyan-600/30'
    },
    purple: {
      iconBg: 'from-purple-500/20 to-purple-600/30',
      iconBorder: 'border-purple-400/30',
      iconText: 'text-purple-400',
      iconTextHover: 'text-purple-300',
      glowBg: 'from-purple-400/20 to-purple-600/30'
    },
    orange: {
      iconBg: 'from-orange-500/20 to-orange-600/30',
      iconBorder: 'border-orange-400/30',
      iconText: 'text-orange-400',
      iconTextHover: 'text-orange-300',
      glowBg: 'from-orange-400/20 to-orange-600/30'
    },
    green: {
      iconBg: 'from-green-500/20 to-green-600/30',
      iconBorder: 'border-green-400/30',
      iconText: 'text-green-400',
      iconTextHover: 'text-green-300',
      glowBg: 'from-green-400/20 to-green-600/30'
    }
  };

  const sizeClasses = sizeConfig[size];
  const colorClasses = colorConfig[color];

  const isStatic = variant === 'static';

  return (
    <motion.div 
      className={`${sizeClasses.container} bg-gradient-to-br ${colorClasses.iconBg} rounded-2xl border ${colorClasses.iconBorder} flex items-center justify-center relative group ${className}`}
      whileHover={isStatic ? {} : { 
        rotateZ: 15,
        scale: 1.05
      }}
      transition={isStatic ? {} : { 
        duration: 0.4,
        type: "spring",
        stiffness: 300
      }}
    >
      {/* Consistent icon shadow/glow */}
      <div className={`absolute inset-0 bg-gradient-to-br ${colorClasses.glowBg} rounded-2xl blur-md ${isStatic ? '' : 'group-hover:blur-lg'} transition-all duration-500`} />
      
      {/* Render either Lucide icon or image */}
      {Icon ? (
        <Icon className={`${sizeClasses.icon} ${colorClasses.iconText} ${isStatic ? '' : `group-hover:${colorClasses.iconTextHover}`} relative z-10 transition-colors duration-300`} />
      ) : src ? (
        <img 
          src={src} 
          alt={alt}
          className={`${sizeClasses.icon} relative z-10 object-contain will-change-transform transform-gpu transition-all duration-300 ${isStatic ? '' : 'group-hover:scale-110'}`}
          loading="lazy"
          decoding="async"
        />
      ) : null}
    </motion.div>
  );
};